import { useReducer, useCallback, useRef } from "react";
import { tokenStorage } from "../../../utils/auth/tokenStorage";
import config from "../../../config/environment";
import { trpc } from "../../../lib/api/trpc";

import { useSupportedFormats } from "./useSupportedFormats";

// --- Types ---

export type BulkFileStatus =
  | "pending"
  | "uploading"
  | "complete"
  | "duplicate"
  | "error";

export type DuplicateInfo = {
  matchType: "content" | "filename";
  filename: string;
  collections: string[];
};

export type BulkFileEntry = {
  id: string;
  file: File;
  status: BulkFileStatus;
  progress: number;
  error: string | null;
  documentId: string | null;
  warning: string | null;
  /** Set when the server 409'd this file as a duplicate (#158). */
  duplicate: DuplicateInfo | null;
  strategyId: string | null;
  /** Supported MIME resolved client-side (browser type, then extension). */
  mimeType: string | null;
};

type BulkUploadState = {
  files: BulkFileEntry[];
  isUploading: boolean;
  /** Epoch ms until which the queue is paused after a 429; null when running. */
  pausedUntil: number | null;
  summary: { succeeded: number; failed: number; duplicates: number } | null;
};

type Action =
  | { type: "ADD_FILES"; entries: BulkFileEntry[] }
  | { type: "REMOVE_FILE"; id: string }
  | { type: "CLEAR_ALL" }
  | { type: "START_UPLOAD" }
  | { type: "FILE_UPLOADING"; id: string }
  | { type: "FILE_PROGRESS"; id: string; progress: number }
  | { type: "FILE_COMPLETE"; id: string; documentId: string }
  | { type: "FILE_DUPLICATE"; id: string; duplicate: DuplicateInfo }
  | { type: "FILE_ERROR"; id: string; error: string }
  | { type: "SET_WARNINGS"; byName: Record<string, string> }
  | { type: "UPLOAD_PAUSED"; resumeAt: number }
  | { type: "UPLOAD_RESUMED" }
  | { type: "UPLOAD_FINISHED" }
  | { type: "SET_STRATEGY"; id: string; strategyId: string };

// --- Reducer ---

function reducer(state: BulkUploadState, action: Action): BulkUploadState {
  switch (action.type) {
    case "ADD_FILES":
      return {
        ...state,
        files: [...state.files, ...action.entries],
        summary: null,
      };
    case "REMOVE_FILE":
      return {
        ...state,
        files: state.files.filter((f) => f.id !== action.id),
      };
    case "CLEAR_ALL":
      return {
        files: [],
        isUploading: false,
        pausedUntil: null,
        summary: null,
      };
    case "START_UPLOAD":
      return { ...state, isUploading: true, summary: null };
    case "UPLOAD_PAUSED":
      return { ...state, pausedUntil: action.resumeAt };
    case "UPLOAD_RESUMED":
      return { ...state, pausedUntil: null };
    case "FILE_UPLOADING":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id
            ? { ...f, status: "uploading" as const, progress: 0 }
            : f,
        ),
      };
    case "FILE_PROGRESS":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id ? { ...f, progress: action.progress } : f,
        ),
      };
    case "FILE_COMPLETE":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id
            ? {
                ...f,
                status: "complete" as const,
                progress: 100,
                documentId: action.documentId,
              }
            : f,
        ),
      };
    case "FILE_DUPLICATE":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id
            ? {
                ...f,
                status: "duplicate" as const,
                duplicate: action.duplicate,
                progress: 0,
              }
            : f,
        ),
      };
    case "FILE_ERROR":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id
            ? { ...f, status: "error" as const, error: action.error }
            : f,
        ),
      };
    case "SET_WARNINGS":
      return {
        ...state,
        files: state.files.map((f) =>
          f.status === "pending" && action.byName[f.file.name]
            ? { ...f, warning: action.byName[f.file.name] }
            : f,
        ),
      };
    case "UPLOAD_FINISHED": {
      const succeeded = state.files.filter(
        (f) => f.status === "complete",
      ).length;
      const failed = state.files.filter((f) => f.status === "error").length;
      const duplicates = state.files.filter(
        (f) => f.status === "duplicate",
      ).length;
      return {
        ...state,
        isUploading: false,
        pausedUntil: null,
        summary: { succeeded, failed, duplicates },
      };
    }
    case "SET_STRATEGY":
      return {
        ...state,
        files: state.files.map((f) =>
          f.id === action.id ? { ...f, strategyId: action.strategyId } : f,
        ),
      };
    default:
      return state;
  }
}

// --- Rate limiting ---

/** Server said 429 — the queue should pause and retry, not fail the file. */
export class RateLimitError extends Error {
  readonly retryAfterSeconds: number;

  constructor(retryAfterSeconds: number) {
    super("Rate limited");
    this.name = "RateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const MIN_RETRY_DELAY_SECONDS = 1;
const MAX_RETRY_DELAY_SECONDS = 120;
const DEFAULT_RETRY_DELAY_SECONDS = 15;
/** Give up on a file after this many consecutive 429 pauses. */
const MAX_RATE_LIMIT_PAUSES_PER_FILE = 10;

function clampRetryDelay(seconds: number): number {
  return Math.min(
    MAX_RETRY_DELAY_SECONDS,
    Math.max(MIN_RETRY_DELAY_SECONDS, Math.ceil(seconds)),
  );
}

/**
 * Work out how long to wait from a 429's headers. Retry-After is preferred
 * (delta-seconds or HTTP-date per RFC 9110); RateLimit-Reset is a fallback
 * whose meaning varies by draft (delta-seconds vs unix timestamp), so large
 * values are treated as timestamps. Anything unreadable gets a safe default.
 */
export function parseRetryAfterSeconds(
  getHeader: (name: string) => string | null,
): number {
  const parse = (value: string | null): number | null => {
    if (!value) return null;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      // Values past ~1e9 can only be unix-timestamp seconds, not a delay
      const delta =
        numeric > 1_000_000_000 ? numeric - Date.now() / 1000 : numeric;
      return delta > 0 ? delta : null;
    }
    const asDate = Date.parse(value);
    if (!Number.isNaN(asDate)) {
      const delta = (asDate - Date.now()) / 1000;
      return delta > 0 ? delta : null;
    }
    return null;
  };

  const seconds =
    parse(getHeader("Retry-After")) ??
    parse(getHeader("RateLimit-Reset")) ??
    DEFAULT_RETRY_DELAY_SECONDS;
  return clampRetryDelay(seconds);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// --- Duplicates (#158) ---

/** Server said 409: same content or filename already uploaded in this org. */
export class DuplicateUploadError extends Error {
  readonly info: DuplicateInfo;

  constructor(info: DuplicateInfo) {
    super("Duplicate document");
    this.name = "DuplicateUploadError";
    this.info = info;
  }
}

/** The pre-check endpoint caps request batches at this size. */
const DUPLICATE_CHECK_BATCH = 500;

type DuplicateCheckResult = {
  filename: string;
  duplicate: boolean;
  existing?: { id: string; filename: string; collections: string[] };
};

/**
 * Advisory pre-flight: which of these filenames already exist in the org?
 * Failures are swallowed — the server still catches duplicates at upload.
 */
async function checkExistingFilenames(
  filenames: string[],
): Promise<Map<string, DuplicateCheckResult>> {
  const found = new Map<string, DuplicateCheckResult>();
  const token = tokenStorage.getAuthToken();
  if (!token) return found;

  for (let i = 0; i < filenames.length; i += DUPLICATE_CHECK_BATCH) {
    const batch = filenames.slice(i, i + DUPLICATE_CHECK_BATCH);
    try {
      const res = await fetch(`${config.api.url}/api/documents/upload/check`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          files: batch.map((filename) => ({ filename })),
        }),
      });
      if (!res.ok) continue;
      const data = (await res.json()) as { results?: DuplicateCheckResult[] };
      for (const result of data.results ?? []) {
        if (result.duplicate) found.set(result.filename, result);
      }
    } catch {
      // Advisory only — never block queueing on a failed pre-check
    }
  }
  return found;
}

// --- Helpers ---

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function validateFile(
  file: File,
  existingNames: Set<string>,
  mimeType: string | null,
  formatsLabel: string,
  maxFileSize: number,
): { error: string | null; warning: string | null } {
  if (!mimeType) {
    return {
      error: `Unsupported file type. Supported formats: ${formatsLabel}`,
      warning: null,
    };
  }
  if (file.size > maxFileSize) {
    return {
      error: `File too large (${formatFileSize(file.size)}). Maximum is ${Math.round(maxFileSize / 1024 / 1024)} MB`,
      warning: null,
    };
  }
  const warning = existingNames.has(file.name)
    ? "A file with this name is already queued"
    : null;
  return { error: null, warning };
}

// --- Hook ---

export const useBulkUpload = () => {
  const [state, dispatch] = useReducer(reducer, {
    files: [],
    isUploading: false,
    pausedUntil: null,
    summary: null,
  });

  const { formatsLabel, resolveMimeType, maxFileSizeBytesFor } =
    useSupportedFormats();

  const utils = trpc.useContext();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const addFiles = useCallback(
    (fileList: FileList | File[]) => {
      const existingNames = new Set(state.files.map((f) => f.file.name));
      const entries: BulkFileEntry[] = [];

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const mimeType = resolveMimeType(file);
        const { error, warning } = validateFile(
          file,
          existingNames,
          mimeType,
          formatsLabel,
          maxFileSizeBytesFor(mimeType),
        );

        entries.push({
          id: generateId(),
          file,
          status: error ? "error" : "pending",
          progress: 0,
          error,
          documentId: null,
          warning,
          duplicate: null,
          strategyId: null,
          mimeType,
        });

        existingNames.add(file.name);
      }

      dispatch({ type: "ADD_FILES", entries });

      // Fire-and-forget advisory pre-check against documents already uploaded
      // (#158) — "just try and you get warnings" without sending any bytes
      const names = entries
        .filter((e) => e.status === "pending")
        .map((e) => e.file.name);
      if (names.length > 0) {
        void checkExistingFilenames(names).then((found) => {
          if (found.size === 0) return;
          const byName: Record<string, string> = {};
          for (const [name, result] of found) {
            const cols = result.existing?.collections ?? [];
            byName[name] =
              `Already in your documents${cols.length > 0 ? ` (${cols.join(", ")})` : ""}`;
          }
          dispatchRef.current({ type: "SET_WARNINGS", byName });
        });
      }
    },
    [state.files, resolveMimeType, formatsLabel, maxFileSizeBytesFor],
  );

  const removeFile = useCallback((id: string) => {
    dispatch({ type: "REMOVE_FILE", id });
  }, []);

  const setFileStrategy = useCallback((id: string, strategyId: string) => {
    dispatch({ type: "SET_STRATEGY", id, strategyId });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: "CLEAR_ALL" });
  }, []);

  const uploadSingleFile = useCallback(
    (
      entry: BulkFileEntry,
      collectionId?: string,
      force = false,
    ): Promise<{ documentId: string }> => {
      return new Promise((resolve, reject) => {
        const token = tokenStorage.getAuthToken();
        if (!token) {
          reject(new Error("Not authenticated"));
          return;
        }

        const formData = new FormData();
        formData.append("file", entry.file);
        if (entry.strategyId) {
          formData.append("extractionStrategyId", entry.strategyId);
        }
        if (collectionId) {
          formData.append("collectionId", collectionId);
        }
        if (force) {
          formData.append("force", "true");
        }

        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${config.api.url}/api/documents/upload`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);

        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            dispatchRef.current({
              type: "FILE_PROGRESS",
              id: entry.id,
              progress,
            });
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status === 429) {
            reject(
              new RateLimitError(
                parseRetryAfterSeconds((name) => xhr.getResponseHeader(name)),
              ),
            );
            return;
          }
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300 && data.document) {
              resolve({ documentId: data.document.id });
            } else if (
              xhr.status === 409 &&
              data.error === "DUPLICATE_DOCUMENT"
            ) {
              reject(
                new DuplicateUploadError({
                  matchType: data.matchType,
                  filename: data.existing?.filename ?? entry.file.name,
                  collections: data.existing?.collections ?? [],
                }),
              );
            } else {
              reject(new Error(data.error || `Upload failed (${xhr.status})`));
            }
          } catch {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Network error"));
        });

        xhr.addEventListener("abort", () => {
          reject(new Error("Upload aborted"));
        });

        xhr.send(formData);
      });
    },
    [],
  );

  const invalidateCaches = useCallback(
    async (collectionId?: string) => {
      // Invalidate caches so document list, quota, and collection counts update
      try {
        // @ts-ignore - tRPC type inference through `as any` router cast
        await utils.documents.list.invalidate();
        // @ts-ignore - tRPC type inference through `as any` router cast
        await utils.documents.getQuota.invalidate();
        if (collectionId) {
          // @ts-ignore - tRPC type inference through `as any` router cast
          await utils.collections.list.invalidate();
        }
      } catch {
        // Cache invalidation failure is non-critical
      }
    },
    [utils],
  );

  /**
   * One entry through to a terminal state. A 429 pauses and retries the same
   * file (bulk uploads finish unattended); a 409 duplicate parks the file for
   * a user decision instead of failing it.
   */
  const uploadEntryWithRetry = useCallback(
    async (
      entry: BulkFileEntry,
      collectionId?: string,
      force = false,
    ): Promise<void> => {
      let rateLimitPauses = 0;
      for (;;) {
        try {
          const result = await uploadSingleFile(entry, collectionId, force);
          dispatch({
            type: "FILE_COMPLETE",
            id: entry.id,
            documentId: result.documentId,
          });
          return;
        } catch (err) {
          if (
            err instanceof RateLimitError &&
            rateLimitPauses < MAX_RATE_LIMIT_PAUSES_PER_FILE
          ) {
            rateLimitPauses++;
            const waitMs = err.retryAfterSeconds * 1000;
            dispatch({ type: "UPLOAD_PAUSED", resumeAt: Date.now() + waitMs });
            await delay(waitMs);
            dispatch({ type: "UPLOAD_RESUMED" });
            continue;
          }
          if (err instanceof DuplicateUploadError) {
            dispatch({
              type: "FILE_DUPLICATE",
              id: entry.id,
              duplicate: err.info,
            });
            return;
          }
          const message =
            err instanceof RateLimitError
              ? "Rate limited repeatedly — please try again later"
              : err instanceof Error
                ? err.message
                : "Unknown error";
          dispatch({ type: "FILE_ERROR", id: entry.id, error: message });
          return;
        }
      }
    },
    [uploadSingleFile],
  );

  // Collection used by the last startUpload, so per-file force retries land
  // in the same place as the rest of their batch.
  const lastCollectionIdRef = useRef<string | undefined>(undefined);

  /** Uploads the queue; every file lands in `collectionId` when provided. */
  const startUpload = useCallback(
    async (collectionId?: string) => {
      lastCollectionIdRef.current = collectionId;
      dispatch({ type: "START_UPLOAD" });

      const pendingFiles = state.files.filter((f) => f.status === "pending");

      for (const entry of pendingFiles) {
        dispatch({ type: "FILE_UPLOADING", id: entry.id });
        await uploadEntryWithRetry(entry, collectionId);
      }

      dispatch({ type: "UPLOAD_FINISHED" });
      await invalidateCaches(collectionId);
    },
    [state.files, uploadEntryWithRetry, invalidateCaches],
  );

  /** "Upload anyway" for one duplicate-parked file (#158). */
  const forceUpload = useCallback(
    async (id: string) => {
      const entry = state.files.find(
        (f) => f.id === id && f.status === "duplicate",
      );
      if (!entry) return;
      dispatch({ type: "FILE_UPLOADING", id });
      await uploadEntryWithRetry(entry, lastCollectionIdRef.current, true);
      await invalidateCaches(lastCollectionIdRef.current);
    },
    [state.files, uploadEntryWithRetry, invalidateCaches],
  );

  const pendingCount = state.files.filter((f) => f.status === "pending").length;

  return {
    files: state.files,
    isUploading: state.isUploading,
    pausedUntil: state.pausedUntil,
    summary: state.summary,
    addFiles,
    removeFile,
    setFileStrategy,
    clearAll,
    startUpload,
    forceUpload,
    pendingCount,
  };
};

export { formatFileSize };
